import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import translations from './translations';

/* ------------------------------------------------------------------ */
/* Regions: currency + display locale + default language per region.   */
/* Rates are approximate USD conversions used only for on-screen       */
/* display — billing remains in USD.                                   */
/* ------------------------------------------------------------------ */
export const REGIONS = {
  US: { label: 'United States', currency: 'USD', rate: 1, locale: 'en-US', lang: 'en' },
  GB: { label: 'United Kingdom', currency: 'GBP', rate: 0.79, locale: 'en-GB', lang: 'en' },
  EU: { label: 'Europe (EUR)', currency: 'EUR', rate: 0.92, locale: 'de-DE', lang: 'en' },
  ZA: { label: 'South Africa', currency: 'ZAR', rate: 18.2, locale: 'en-ZA', lang: 'en' },
  IN: { label: 'India', currency: 'INR', rate: 83, locale: 'en-IN', lang: 'en' },
  AU: { label: 'Australia', currency: 'AUD', rate: 1.5, locale: 'en-AU', lang: 'en' },
  CA: { label: 'Canada', currency: 'CAD', rate: 1.36, locale: 'en-CA', lang: 'en' },
  BR: { label: 'Brazil', currency: 'BRL', rate: 5.4, locale: 'pt-BR', lang: 'pt' },
  MX: { label: 'Mexico', currency: 'MXN', rate: 17.5, locale: 'es-MX', lang: 'es' },
  JP: { label: 'Japan', currency: 'JPY', rate: 155, locale: 'ja-JP', lang: 'en' },
  NG: { label: 'Nigeria', currency: 'NGN', rate: 1450, locale: 'en-NG', lang: 'en' },
  KE: { label: 'Kenya', currency: 'KES', rate: 129, locale: 'en-KE', lang: 'en' },
};

export const LANGUAGES = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
};

/* Countries that use the euro — mapped onto the EU region. */
const EURO_COUNTRIES = new Set([
  'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE',
  'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK',
]);

/* Coarse timezone → country hints for when the browser language has
   no region subtag (e.g. plain "en"). */
const TZ_HINTS = [
  ['Africa/Johannesburg', 'ZA'],
  ['Africa/Lagos', 'NG'],
  ['Africa/Nairobi', 'KE'],
  ['Europe/London', 'GB'],
  ['Europe/Paris', 'FR'],
  ['Europe/Berlin', 'DE'],
  ['Europe/Madrid', 'ES'],
  ['Europe/Rome', 'IT'],
  ['Europe/Lisbon', 'PT'],
  ['Europe/Amsterdam', 'NL'],
  ['Europe/Dublin', 'IE'],
  ['America/Sao_Paulo', 'BR'],
  ['America/Mexico_City', 'MX'],
  ['America/Toronto', 'CA'],
  ['America/Vancouver', 'CA'],
  ['Australia/', 'AU'],
  ['Asia/Tokyo', 'JP'],
  ['Asia/Kolkata', 'IN'],
];

function countryToRegion(cc) {
  if (!cc) return null;
  if (REGIONS[cc]) return cc;
  if (EURO_COUNTRIES.has(cc)) return 'EU';
  return null;
}

function detectRegion() {
  try {
    // 1) Region subtag of the browser language, e.g. "en-ZA" → ZA
    const langs = navigator.languages || [navigator.language || 'en-US'];
    for (const l of langs) {
      const parts = l.split('-');
      if (parts[1]) {
        const r = countryToRegion(parts[1].toUpperCase());
        if (r) return r;
      }
    }
    // 2) Timezone hint
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    for (const [prefix, cc] of TZ_HINTS) {
      if (tz.startsWith(prefix)) {
        const r = countryToRegion(cc);
        if (r) return r;
      }
    }
  } catch (e) {
    /* fall through */
  }
  return 'US';
}

function detectLanguage(regionKey) {
  try {
    const primary = (navigator.language || 'en').split('-')[0].toLowerCase();
    if (LANGUAGES[primary]) return primary;
  } catch (e) {
    /* fall through */
  }
  return REGIONS[regionKey]?.lang || 'en';
}

/* Round converted prices to friendly local numbers. */
function niceRound(v) {
  if (v >= 100000) return Math.round(v / 5000) * 5000;
  if (v >= 10000) return Math.round(v / 500) * 500;
  if (v >= 1000) return Math.round(v / 100) * 100;
  return Math.round(v / 10) * 10;
}

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [region, setRegion] = useState(() => {
    try {
      const saved = localStorage.getItem('st-region');
      if (saved && REGIONS[saved]) return saved;
    } catch (e) { /* ignore */ }
    return detectRegion();
  });

  const [language, setLanguage] = useState(() => {
    try {
      const saved = localStorage.getItem('st-lang');
      if (saved && LANGUAGES[saved]) return saved;
    } catch (e) { /* ignore */ }
    return detectLanguage(detectRegion());
  });

  useEffect(() => {
    try { localStorage.setItem('st-region', region); } catch (e) { /* ignore */ }
  }, [region]);

  useEffect(() => {
    try { localStorage.setItem('st-lang', language); } catch (e) { /* ignore */ }
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(() => {
    const r = REGIONS[region] || REGIONS.US;
    const dict = translations[language] || translations.en;

    const t = (key) => dict[key] ?? translations.en[key] ?? key;

    const formatPrice = (usd) => {
      if (usd == null) return null; // "Custom" plans
      const local = niceRound(usd * r.rate);
      try {
        return new Intl.NumberFormat(r.locale, {
          style: 'currency',
          currency: r.currency,
          maximumFractionDigits: 0,
        }).format(local);
      } catch (e) {
        return `${r.currency} ${local.toLocaleString()}`;
      }
    };

    return { region, setRegion, language, setLanguage, t, formatPrice, currency: r.currency, regionInfo: r };
  }, [region, language]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>');
  return ctx;
}
